import React, { useEffect, useCallback, memo, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import type { TextbookListItem } from '@/features/Quiz/api/getTextbooks';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { LeftCreateQuiz } from '../LeftCreateQuiz';
import { LeftCreateQuizV2 } from '../LeftCreateQuizV2';
import { RightQuiz } from '../RightQuiz';
import { QuizMathJaxProvider } from '@/components/ReactMathJax';
import { Quiz, QuizFormData } from '../../types';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { Reorder } from '@/utils/helpers';
import { createNewQuiz, editQuiz, QuizType } from '@/redux/reducers/quizzes/quizSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useNotificationStore } from '@/stores/notifications';
import { createNewQuizLibrary, editQuizLibrary } from '@/redux/reducers/quizzes/quizLibrary';
import { createQuizAdmin, editQuizAdmin } from '@/redux/reducers/quizzes/quizAdmin';
import { createAdminQuizV2, updateAdminQuizV2, updateUserQuizV2 } from '@/features/Quiz/api/getQuizzes';
import { updateLibraryQuizV2, createLibraryQuizV2 } from '@/features/Quiz/api/libraryQuizDetailV2';
import type { LibraryQuizDetailsV2 } from '@/features/Quiz/api/libraryQuizDetailV2';
import { buildQuizV2CreatePayload } from '@/features/Quiz/utils/buildQuizV2CreatePayload';
import { buildQuizV2UpdatePayload } from '@/features/Quiz/utils/quizV2AdminEdit';
import type { AdminQuizDetailsContent } from '@/features/Quiz/utils/quizV2AdminEdit';
import { buildLibraryQuizV2UpdatePayload, buildLibraryQuizV2CreatePayload } from '@/features/Quiz/utils/quizV2Library';
import { getQuizV2BasePath } from '@/features/Quiz/utils/quizV2BasePath';
import { afterLibraryQuizSavePath } from '@/features/Quiz/api/syncLibraryQuizV2';
import { createQuizUser, editQuizUser } from '@/redux/reducers/quizzes/quizUser';
import { useEducationFeatures } from '@/features/FeatureFlags/api/getFeatureFlagsList';

const searchInputSchema = (isLibrary: boolean) => {
    if (isLibrary) {
        return yup.object().shape({
            title: yup.string(),
        });
    }

    return yup.object().shape({
        title: yup.string(),
        isGraded: yup.string(),
    });
};

type Props = {
  quiz?: QuizType | null;
  isLibrary?: boolean;
  isAdminQuiz?: boolean;
  /** When true with admin quiz, create via POST /api/v2/admin/quiz/create (quiz-svc). */
  isAdminQuizV2?: boolean;
  /** My Quizzes (current user) V2 create: POST /api/v2/admin/quiz/create (quiz-svc). */
  isMyQuizV2?: boolean;
  /** Customer (instructor) quiz V2: GET userQuiz + PUT update/userQuiz. */
  isCustomerQuizV2?: boolean;
  /** Library quiz V2: GET/PATCH /api/v2/admin/quiz/library/{uuid}. */
  isLibraryQuizV2?: boolean;
  /** Raw GET details response for V2 edit (IDs for PUT). */
  adminQuizV2Details?: AdminQuizDetailsContent | null;
  libraryQuizV2Details?: LibraryQuizDetailsV2 | null;
  /** libraryType to use on new-create (from URL param). Ignored on edit/copy. */
    defaultLibraryType?: 'TEXTBOOK' | 'CHAPTER' | 'VIDEO';
  isEdit?: boolean;
  isUserQuiz?: boolean;
  /** Select Article picker uses V2 library article_list. */
  libraryV2Select?: boolean;
};

export const FormQuiz = memo(
  ({
    quiz,
    isLibrary = false,
    isAdminQuiz = false,
    isAdminQuizV2 = false,
    isMyQuizV2 = false,
    isCustomerQuizV2 = false,
    isLibraryQuizV2 = false,
    adminQuizV2Details = null,
    libraryQuizV2Details = null,
    defaultLibraryType = 'TEXTBOOK',
    isEdit = false,
    isUserQuiz = false,
    libraryV2Select = false,
  }: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { pathname } = useLocation();
    const isCustomerQuiz = pathname.includes("customer-quiz");
    const { data: featureData } = useEducationFeatures();
    const useVideoLibraryV2Create = !!featureData?.enableOpenSearchForQuizLibrary;

    const { addNotification } = useNotificationStore();
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedTextbook, setSelectedTextbook] = useState<TextbookListItem | null>(null);
    const [textbookError, setTextbookError] = useState<string | undefined>();
    const [articleError, setArticleError] = useState<string | undefined>();
    /** Library quiz V2 (video/chapter/textbook) Draft/Active status. Not tracked via react-hook-form to avoid TS complexity limits. */
    const [status, setStatus] = useState<'ACTIVE' | 'DRAFT'>(() =>
        isLibraryQuizV2 && quiz ? (quiz.active ? 'ACTIVE' : 'DRAFT') : 'ACTIVE'
    );

    const effectiveLibraryType: 'TEXTBOOK' | 'CHAPTER' | 'VIDEO' = (() => {
        const fromDetails = libraryQuizV2Details?.libraryType;
        if (fromDetails === 'CHAPTER' || fromDetails === 'VIDEO' || fromDetails === 'TEXTBOOK') {
            return fromDetails;
        }
        return defaultLibraryType;
    })();
    /** Publisher + textbook filters: TEXTBOOK library only (create, edit, copy). */
    const showTextbookPicker = isLibraryQuizV2 && effectiveLibraryType === 'TEXTBOOK';
    /** Brand-new TEXTBOOK create (no source quiz details) — textbook selection required for meta. */
    const isNewTextbookLibraryV2 = showTextbookPicker && !libraryQuizV2Details;
    const isNewVideoLibraryV2 =
        isLibraryQuizV2 && !isEdit && !libraryQuizV2Details && effectiveLibraryType === 'VIDEO';
    const isVideoLibraryV2 = isLibraryQuizV2 && effectiveLibraryType === 'VIDEO';
    const libraryQuizListPath = isVideoLibraryV2 ? '/quiz' : '/quiz/library';
    const initFormValues = useCallback(
      (data: (Omit<Quiz, "isGraded"> & { isGraded?: boolean }) | null) => {
        return {
            article: data?.article || null,
            articleSectionId: null,
            articleTitle: null,
            title: data?.title ?? '',
            tags: data?.tags?.map((tag) => tag.id) ?? [],
            ...(isAdminQuiz || isLibrary || isUserQuiz
                ? {
                      isGraded: typeof data?.isGraded === 'boolean' ? (data?.isGraded ? 'graded' : 'ungraded') : '',
                      startDate: data?.startDate ? new Date(data.startDate) : new Date(),
                      endDate: data?.endDate ? new Date(data.endDate) : null,
                      duration: data?.duration ?? 0,
                      showCaption: data ? data.showCaption : true,
                  }
                : {}),
            ...(isUserQuiz
                ? {
                      restrictNonInstitutional: data?.restrictNonInstitutional ?? false,
                  }
                : {}),
            activities:
                data?.activities && data.activities.length > 0
                    ? [...data.activities]
                          .sort((a, b) => a.sortIndex - b.sortIndex)
                          .map((activity, index) => {
                              const { id: customId, originId, items, ...dataActivity } =
                                  activity as typeof activity & { id?: number };

                      return {
                        ...dataActivity,
                        originId: originId ?? customId ?? null,
                        isSelect: index === 0,
                        sortIndex: index + 1,
                        items:
                          items && items.length > 0
                            ? [...items]
                                  .sort((a, b) => a.sortIndex - b.sortIndex)
                                  .map((item, indexItem) => {
                                    const { id: customItemId, originId: itemOriginId, ...dataItem } =
                                        item as typeof item & { id?: number };

                                                    return {
                                                        ...dataItem,
                                                        originId: itemOriginId ?? customItemId ?? null,
                                                        isSelect: indexItem === 0,
                                                        sortIndex: indexItem + 1,
                                                        isMultiple: dataItem.isMultiple ?? false,
                                                        rightAnswer: dataItem.rightAnswer ?? [1],
                                                        textOptions: dataItem.textOptions
                                                            ? dataItem.textOptions.map((textOption) => {
                                                                  const { id: customTextOptionId, ...dataTextOption } = textOption;
                                                                  return {
                                                                      ...dataTextOption,
                                                                      originId:
                                                                          (textOption as { id: number; text: string; originId?: number }).originId ??
                                                                          customTextOptionId,
                                                                  };
                                                              })
                                                            : [
                                                                  {
                                                                      originId: 1,
                                                                      text: '',
                                                                  },
                                                                  {
                                                                      originId: 2,
                                                                      text: '',
                                                                  },
                                                                  {
                                                                      originId: 3,
                                                                      text: '',
                                                                  },
                                                                  {
                                                                      originId: 4,
                                                                      text: '',
                                                                  },
                                                              ],
                                                    };
                                                })
                                          : [],
                              };
                          })
                    : [
                          {
                              originId: null,
                              title: isAdminQuizV2 || isLibraryQuizV2 || isMyQuizV2 ? 'Section 1' : 'Activity 1',
                              sortIndex: 1,
                              isSelect: true,
                              items: [],
                          },
                      ],
        };
      },
      [isAdminQuizV2, isLibraryQuizV2, isMyQuizV2]
    );

    const { handleSubmit, ...methods } = useForm<QuizFormData>({
      mode: "onSubmit",
      resolver: yupResolver(
        searchInputSchema(isLibrary || isAdminQuiz || isUserQuiz)
      ),
      defaultValues: initFormValues(quiz ?? null),
    });

    const { replace } = useFieldArray<QuizFormData, "activities">({
      control: methods.control,
      name: "activities",
    });

    const activities = methods.watch("activities") ?? [];
    const selectedActivityIdx = activities.findIndex((a) => a.isSelect);
    const safeActivityIdx = selectedActivityIdx === -1 ? 0 : selectedActivityIdx;
    const indexActivity =
      activities.length === 0
        ? 0
        : Math.min(safeActivityIdx, activities.length - 1);
    const items = activities[indexActivity]?.items ?? [];

    const { replace: replaceItems } = useFieldArray<
      QuizFormData,
      `activities.${number}.items`
    >({
      control: methods.control,
      name: `activities.${indexActivity}.items`,
    });

    useEffect(() => {
        quiz?.title && methods.setValue('title', quiz.title);
        quiz?.activities &&
            methods.setValue(
                'activities',
                quiz?.activities && quiz.activities.length > 0
                    ? [...quiz.activities]
                          .sort((a, b) => a.sortIndex - b.sortIndex)
                          .map((activity, index) => {
                              const { id: customId, originId, items, ...dataActivity } =
                                  activity as typeof activity & { id?: number };

                              return {
                                  ...dataActivity,
                                  originId: originId ?? customId ?? null,
                                  isSelect: index === 0,
                                  sortIndex: index + 1,
                                  items:
                                      items && items.length > 0
                                          ? [...items]
                                                .sort((a, b) => a.sortIndex - b.sortIndex)
                                                .map((item, indexItem) => {
                                                    const { id: customItemId, originId: itemOriginId, ...dataItem } =
                                                        item as typeof item & { id?: number };

                                                    return {
                                                        ...dataItem,
                                                        originId: itemOriginId ?? customItemId ?? null,
                                                        isSelect: indexItem === 0,
                                                        sortIndex: indexItem + 1,
                                                        isMultiple: dataItem.isMultiple ?? false,
                                                        rightAnswer: dataItem.rightAnswer ?? [1],
                                                        textOptions: dataItem.textOptions
                                                            ? dataItem.textOptions.map((textOption) => {
                                                                  const { id: customTextOptionId, ...dataTextOption } = textOption;
                                                                  return {
                                                                      ...dataTextOption,
                                                                      originId:
                                                                          (textOption as { id: number; text: string; originId?: number }).originId ??
                                                                          customTextOptionId,
                                                                  };
                                                              })
                                                            : [
                                                                  {
                                                                      originId: 1,
                                                                      text: '',
                                                                  },
                                                                  {
                                                                      originId: 2,
                                                                      text: '',
                                                                  },
                                                                  {
                                                                      originId: 3,
                                                                      text: '',
                                                                  },
                                                                  {
                                                                      originId: 4,
                                                                      text: '',
                                                                  },
                                                              ],
                                                    };
                                                })
                                          : [],
                              };
                          })
                    : []
            );
    }, [quiz?.title, quiz?.activities]);

    const onDragEnd = useCallback(
      (result: DropResult) => {
        if (!result.destination) {
          return;
        }

        if (result.type === "ACTIVITIES") {
          const list = Reorder(
            activities,
            result.source.index,
            result.destination.index
          );

          replace(list);
        } else {
          const list = Reorder(
            items,
            result.source.index,
            result.destination.index
          );

          replaceItems(list);
        }
      },
      [replace, replaceItems, activities, items]
    );

    const onSubmit = useCallback(async () => {
        const { isGraded, activities, startDate, endDate, duration, showCaption, restrictNonInstitutional, ...rest } = methods.getValues();
        const { title } = methods.getValues();

        let alertMessage = '';

        if (isEdit) {
            for (const activity of activities) {
                for (const item of activity.items) {
                    const arr: string[] = [];

                    if (item.article === null) {
                        for (const options of item.textOptions) {
                            arr.push(options.text);
                            const hasDuplicates = () => new Set(arr).size !== arr.length;
                            if (alertMessage) break;

                            if (!options.text) {
                                alertMessage = alertMessage + 'Options should not be empty';
                                break;
                            } else if (hasDuplicates()) {
                                alertMessage = alertMessage + 'Options should not be duplicated';
                                break;
                            }
                        }
                    }
                }
            }
        }

        if (title.trim().length === 0) {
            alertMessage = alertMessage + 'No Title\n';
        } else if (title.trim().length < 3) {
            alertMessage = alertMessage + 'Title is too short. It should have 3 characters or more\n';
        }

        if ((isLibrary || isAdminQuiz || isUserQuiz) && isGraded.length === 0) {
            alertMessage = alertMessage + 'No type selected\n';
        }

        const runCreateLikeSectionValidation =
            !isEdit ||
            (isAdminQuizV2 && isEdit && isAdminQuiz) ||
            (isCustomerQuizV2 && isEdit) ||
            (isLibraryQuizV2 && isEdit);
        if (runCreateLikeSectionValidation) {
            for (const activity of activities) {
                if (activity.items.length === 0) {
                    const sectionOrActivityMsg =
                        isAdminQuizV2 || isCustomerQuizV2 || isLibraryQuizV2 || isMyQuizV2
                        ? 'Each section should have at least one question\n'
                        : 'Each activity should have at least one question\n';
                    const emptyMsg = isLibrary ? sectionOrActivityMsg : 'There should be at least one question\n';
                    alertMessage = alertMessage + emptyMsg;
                    break;
                } else {
                    if (
                        (isAdminQuizV2 || isCustomerQuizV2 || isLibraryQuizV2 || isMyQuizV2) &&
                        !activity.items.some((i) => i.type === 2)
                    ) {
                        alertMessage =
                            alertMessage + 'Each section should have at least one multiple-choice question\n';
                        break;
                    }
                    for (const item of activity.items) {
                        if (item.title.trim().length === 0) {
                            alertMessage = alertMessage + 'Question title should not be empty\n';
                            break;
                        } else if (item.title.trim().length < 3) {
                            alertMessage = alertMessage + 'Question title is too short. It should have 3 characters or more\n';
                            break;
                        }
                    }
                }
            }
        }

        if (alertMessage.length > 0) {
            addNotification({
                type: 'error',
                title: alertMessage,
            });

            return;
        }

        setIsSubmitting(true);

        if (isLibrary) {
            if (isLibraryQuizV2 && isEdit && libraryQuizV2Details) {
                try {
                    if (
                        showTextbookPicker &&
                        !selectedTextbook &&
                        libraryQuizV2Details.joveSectionId == null
                    ) {
                        setTextbookError('Please select a textbook before saving.');
                        setIsSubmitting(false);
                        return;
                    }
                    setTextbookError(undefined);
                    const payload = {
                        ...buildLibraryQuizV2UpdatePayload({ ...methods.getValues(), status }, libraryQuizV2Details),
                        ...(showTextbookPicker
                            ? {
                                  joveSectionId:
                                      selectedTextbook?.joveSectionId ??
                                      libraryQuizV2Details.joveSectionId,
                              }
                            : {}),
                    };
                    if (payload.sections.length === 0) {
                        addNotification({
                            type: 'error',
                            title: 'Add at least one multiple-choice question in a section.',
                        });
                        setIsSubmitting(false);
                        return;
                    }
                    await updateLibraryQuizV2(libraryQuizV2Details.quiz.uuid, payload);
                    setIsSubmitting(false);
                    navigate(
                        afterLibraryQuizSavePath(
                            libraryQuizV2Details.quiz.uuid,
                            libraryQuizV2Details.locale,
                            libraryQuizV2Details.libraryType,
                            libraryQuizListPath
                        ),
                        { replace: true }
                    );
                } catch (err: unknown) {
                    setIsSubmitting(false);
                    const ax = err as { response?: { data?: { message?: string } }; message?: string };
                    const msg =
                        ax?.response?.data?.message ??
                        (typeof ax?.message === 'string' ? ax.message : null) ??
                        'Failed to update quiz';
                    addNotification({
                        type: 'error',
                        title: typeof msg === 'string' ? msg : 'Failed to update quiz',
                    });
                }
                return;
            }
            if (quiz && isEdit) {
                const action = await dispatch(
                    editQuizLibrary({
                        id: quiz.id,
                        data: {
                            ...rest,
                            startDate: startDate?.toISOString() ?? '',
                            endDate: endDate?.toISOString() ?? null,
                            isGraded: isGraded === 'graded',
                            duration: duration === 0 ? null : duration,
                            activities:
                                activities?.map((activity) => {
                                    const { title, sortIndex, items, originId } = activity;

                                    return {
                                        id: originId,
                                        title,
                                        sortIndex,
                                        items: items?.map((item) => {
                                            const {
                                                article,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions,
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                                originId: originIdItem,
                                            } = item;

                                            if (type === 1) {
                                                return {
                                                    id: originIdItem,
                                                    article: article?.videoData.articleId ?? null,
                                                    sortIndex: sortIndexItem,
                                                    title: titleItem,
                                                    type,
                                                    isMultiple,
                                                    image,
                                                };
                                            }

                                            return {
                                                id: originIdItem,
                                                article: article?.videoData.articleId ?? null,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions: textOptions.map((textOption) => ({
                                                    id: textOption.originId,
                                                    text: textOption.text,
                                                })),
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                            };
                                        }),
                                    };
                                }) ?? [],
                        },
                    })
                );

                if (isFulfilled(action)) {
                    setIsSubmitting(false);
                    navigate(`/quiz/library`, {
                        replace: true,
                    });
                }

                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    }
                }
            } else if (isLibraryQuizV2) {
                try {
                    if (
                        showTextbookPicker &&
                        !selectedTextbook &&
                        libraryQuizV2Details?.joveSectionId == null
                    ) {
                        setTextbookError('Please select a textbook before saving.');
                        setIsSubmitting(false);
                        return;
                    }
                    const formValues = methods.getValues();
                    if (isNewVideoLibraryV2 && (!formValues.article || !formValues.articleSectionId)) {
                        setArticleError('Please select an article before saving.');
                        setIsSubmitting(false);
                        return;
                    }
                    setTextbookError(undefined);
                    setArticleError(undefined);
                    const meta = libraryQuizV2Details
                        ? {
                              libraryType: libraryQuizV2Details.libraryType,
                              joveSectionId:
                                  showTextbookPicker
                                      ? selectedTextbook?.joveSectionId ??
                                        libraryQuizV2Details.joveSectionId
                                      : libraryQuizV2Details.joveSectionId,
                              locale: libraryQuizV2Details.locale,
                          }
                        : isNewTextbookLibraryV2
                          ? {
                                libraryType: 'TEXTBOOK' as const,
                                joveSectionId: selectedTextbook?.joveSectionId ?? null,
                                joveDomainId: null,
                                locale: 'en',
                            }
                          : isNewVideoLibraryV2
                            ? {
                                  libraryType: 'VIDEO' as const,
                                  joveDomainId: formValues.article ?? null,
                                  joveSectionId: formValues.articleSectionId ?? null,
                                  locale: 'en',
                              }
                            : {
                                  libraryType: 'CHAPTER' as const,
                                  joveSectionId: null,
                                  joveDomainId: null,
                                  locale: 'en',
                              };
                    const payload = buildLibraryQuizV2CreatePayload({ ...formValues, status }, meta);
                    if (payload.sections.length === 0) {
                        addNotification({
                            type: 'error',
                            title: 'Add at least one multiple-choice question in a section.',
                        });
                        setIsSubmitting(false);
                        return;
                    }
                    const created = await createLibraryQuizV2(payload);
                    await queryClient.invalidateQueries(['library-quiz-v2']);
                    setIsSubmitting(false);
                    navigate(
                        afterLibraryQuizSavePath(
                            created?.quiz?.uuid,
                            payload.locale ?? created?.locale ?? 'en',
                            payload.libraryType ?? created?.libraryType,
                            libraryQuizListPath
                        ),
                        { replace: true }
                    );
                } catch (err: unknown) {
                    setIsSubmitting(false);
                    const ax = err as { response?: { data?: { message?: string } }; message?: string };
                    const msg =
                        ax?.response?.data?.message ??
                        (typeof ax?.message === 'string' ? ax.message : null) ??
                        'Failed to create quiz';
                    addNotification({
                        type: 'error',
                        title: typeof msg === 'string' ? msg : 'Failed to create quiz',
                    });
                }
                return;
            } else {
                const action = await dispatch(
                    createNewQuizLibrary({
                        ...rest,
                        startDate: startDate?.toISOString() ?? '',
                        endDate: endDate?.toISOString() ?? '',
                        isGraded: isGraded === 'graded',
                        duration: duration === 0 ? null : duration,
                        activities: activities.map((activity) => {
                            const { title, sortIndex, items } = activity;

                            return {
                                title,
                                sortIndex,
                                items: items.map((item) => {
                                    const {
                                        article,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions,
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    } = item;

                                    if (type === 1) {
                                        return {
                                            article: article?.videoData.articleId ?? null,
                                            sortIndex: sortIndexItem,
                                            title: titleItem,
                                            textOptions: [],
                                            type,
                                            isMultiple,
                                            rightAnswer: [],
                                            image,
                                        };
                                    }

                                    return {
                                        article: article?.videoData.articleId ?? null,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions: textOptions.map((textOption) => ({
                                            id: textOption.originId,
                                            text: textOption.text,
                                        })),
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    };
                                }),
                            };
                        }),
                    })
                );

                if (isFulfilled(action) && action.payload) {
                    setIsSubmitting(false);
                    navigate(`/quiz/library`, {
                        replace: true,
                    });
                }
                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    }
                }
            }
        } else if (isAdminQuiz) {
            if (isAdminQuizV2 && isEdit && adminQuizV2Details) {
                try {
                    const payload = buildQuizV2UpdatePayload(methods.getValues(), adminQuizV2Details);
                    if (payload.sections.length === 0) {
                        addNotification({
                            type: 'error',
                            title: 'Add at least one multiple-choice question in a section.',
                        });
                        setIsSubmitting(false);
                        return;
                    }
                    await updateAdminQuizV2(adminQuizV2Details.quiz.uuid, payload);
                    setIsSubmitting(false);
                    navigate(`/quiz/admin-quiz-v2`, {
                        replace: true,
                    });
                } catch (err: unknown) {
                    setIsSubmitting(false);
                    const ax = err as { response?: { data?: { message?: string } }; message?: string };
                    const msg =
                        ax?.response?.data?.message ??
                        (typeof ax?.message === 'string' ? ax.message : null) ??
                        'Failed to update quiz';
                    addNotification({
                        type: 'error',
                        title: typeof msg === 'string' ? msg : 'Failed to update quiz',
                    });
                }
                return;
            }
            if (isAdminQuizV2 && !isEdit) {
                try {
                    const payload = buildQuizV2CreatePayload(methods.getValues());
                    if (payload.sections.length === 0) {
                        addNotification({
                            type: 'error',
                            title: 'Add at least one multiple-choice question in a section.',
                        });
                        setIsSubmitting(false);
                        return;
                    }
                    await createAdminQuizV2(payload);
                    setIsSubmitting(false);
                    navigate(`/quiz/admin-quiz-v2`, {
                        replace: true,
                    });
                } catch (err: unknown) {
                    setIsSubmitting(false);
                    const ax = err as { response?: { data?: { message?: string } }; message?: string };
                    const msg =
                        ax?.response?.data?.message ??
                        (typeof ax?.message === 'string' ? ax.message : null) ??
                        'Failed to create quiz';
                    addNotification({
                        type: 'error',
                        title: typeof msg === 'string' ? msg : 'Failed to create quiz',
                    });
                }
                return;
            }
            if (quiz && isEdit) {
                const action = await dispatch(
                    editQuizAdmin({
                        id: quiz.id,
                        data: {
                            ...rest,
                            startDate: startDate?.toISOString() ?? '',
                            endDate: endDate?.toISOString() ?? null,
                            isGraded: isGraded === 'graded',
                            duration: duration === 0 ? null : duration,
                            activities:
                                activities?.map((activity) => {
                                    const { title, sortIndex, items, originId } = activity;

                                    return {
                                        id: originId,
                                        title,
                                        sortIndex,
                                        items: items?.map((item) => {
                                            const {
                                                article,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions,
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                                originId: originIdItem,
                                            } = item;

                                            if (type === 1) {
                                                return {
                                                    id: originIdItem,
                                                    article: article?.videoData.articleId ?? null,
                                                    sortIndex: sortIndexItem,
                                                    title: titleItem,
                                                    type,
                                                    isMultiple,
                                                    image,
                                                };
                                            }

                                            return {
                                                id: originIdItem,
                                                article: article?.videoData.articleId ?? null,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions: textOptions.map((textOption) => ({
                                                    id: textOption.originId,
                                                    text: textOption.text,
                                                })),
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                            };
                                        }),
                                    };
                                }) ?? [],
                        },
                    })
                );

                if (isFulfilled(action)) {
                    setIsSubmitting(false);
                    navigate(`/quiz/admin-quiz`, {
                        replace: true,
                    });
                }

                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    }
                }
            } else {
                const action = await dispatch(
                    createQuizAdmin({
                        ...rest,
                        startDate: startDate?.toISOString() ?? '',
                        endDate: endDate?.toISOString() ?? '',
                        isGraded: isGraded === 'graded',
                        duration: duration === 0 ? null : duration,
                        activities: activities.map((activity) => {
                            const { title, sortIndex, items } = activity;

                            return {
                                title,
                                sortIndex,
                                items: items.map((item) => {
                                    const {
                                        article,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions,
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    } = item;

                                    if (type === 1) {
                                        return {
                                            article: article?.videoData.articleId ?? null,
                                            sortIndex: sortIndexItem,
                                            title: titleItem,
                                            textOptions: [],
                                            type,
                                            isMultiple,
                                            rightAnswer: [],
                                            image,
                                        };
                                    }

                                    return {
                                        article: article?.videoData.articleId ?? null,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions: textOptions.map((textOption) => ({
                                            id: textOption.originId,
                                            text: textOption.text,
                                        })),
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    };
                                }),
                            };
                        }),
                    })
                );

                if (isFulfilled(action) && action.payload) {
                    setIsSubmitting(false);
                    navigate(`/quiz/admin-quiz`, {
                        replace: true,
                    });
                }

                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    }
                }
            }
        } else if (isUserQuiz) {
            if (isCustomerQuizV2 && isEdit && adminQuizV2Details) {
                try {
                    const payload = buildQuizV2UpdatePayload(methods.getValues(), adminQuizV2Details, {
                        applyUserQuizAccessType: true,
                    });
                    if (payload.sections.length === 0) {
                        addNotification({
                            type: 'error',
                            title: 'Add at least one multiple-choice question in a section.',
                        });
                        setIsSubmitting(false);
                        return;
                    }
                    await updateUserQuizV2(adminQuizV2Details.quiz.uuid, payload);
                    setIsSubmitting(false);
                    navigate(getQuizV2BasePath(pathname), {
                        replace: true,
                    });
                } catch (err: unknown) {
                    setIsSubmitting(false);
                    const ax = err as { response?: { data?: { message?: string } }; message?: string };
                    const msg =
                        ax?.response?.data?.message ??
                        (typeof ax?.message === 'string' ? ax.message : null) ??
                        'Failed to update quiz';
                    addNotification({
                        type: 'error',
                        title: typeof msg === 'string' ? msg : 'Failed to update quiz',
                    });
                }
                return;
            }
            if (isMyQuizV2 && !isEdit) {
                try {
                    const payload = buildQuizV2CreatePayload(methods.getValues(), {
                        applyUserQuizAccessType: true,
                    });
                    if (payload.sections.length === 0) {
                        addNotification({
                            type: 'error',
                            title: 'Add at least one multiple-choice question in a section.',
                        });
                        setIsSubmitting(false);
                        return;
                    }
                    await createAdminQuizV2(payload);
                    setIsSubmitting(false);
                    navigate('/quiz/my-quiz', {
                        replace: true,
                    });
                } catch (err: unknown) {
                    setIsSubmitting(false);
                    const ax = err as { response?: { data?: { message?: string } }; message?: string };
                    const msg =
                        ax?.response?.data?.message ??
                        (typeof ax?.message === 'string' ? ax.message : null) ??
                        'Failed to create quiz';
                    addNotification({
                        type: 'error',
                        title: typeof msg === 'string' ? msg : 'Failed to create quiz',
                    });
                }
                return;
            }
            if (quiz && isEdit) {
                const action = await dispatch(
                    editQuizUser({
                        id: quiz.id,
                        data: {
                            ...rest,
                            startDate: startDate?.toISOString() ?? '',
                            endDate: endDate?.toISOString() ?? null,
                            isGraded: isGraded === 'graded',
                            duration: duration === 0 ? null : duration,
                            showCaption: Boolean(showCaption),
                            restrictNonInstitutional: restrictNonInstitutional ?? false,
                            activities:
                                activities?.map((activity) => {
                                    const { title, sortIndex, items, originId } = activity;

                                    return {
                                        id: originId,
                                        title,
                                        sortIndex,
                                        items: items?.map((item) => {
                                            const {
                                                article,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions,
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                                originId: originIdItem,
                                            } = item;

                                            if (type === 1) {
                                                return {
                                                    id: originIdItem,
                                                    article: article?.videoData.articleId ?? null,
                                                    sortIndex: sortIndexItem,
                                                    title: titleItem,
                                                    type,
                                                    isMultiple,
                                                    image,
                                                };
                                            }

                                            return {
                                                id: originIdItem,
                                                article: article?.videoData.articleId ?? null,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions: textOptions.map((textOption) => ({
                                                    id: textOption.originId,
                                                    text: textOption.text,
                                                })),
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                            };
                                        }),
                                    };
                                }) ?? [],
                        },
                    })
                );

                if (isFulfilled(action)) {
                    setIsSubmitting(false);
                    navigate(isCustomerQuiz ? `/quiz/customer-quiz` : `/quiz`, {
                        replace: true,
                    });
                }
                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content && action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    } else if (action.payload.message) {
                        addNotification({
                            type: 'error',
                            title: action.payload.message,
                        });
                    }
                }
            } else {
                const action = await dispatch(
                    createQuizUser({
                        ...rest,
                        startDate: startDate?.toISOString() ?? '',
                        endDate: endDate?.toISOString() ?? '',
                        isGraded: isGraded === 'graded',
                        duration: duration === 0 ? null : duration,
                        restrictNonInstitutional: false,
                        activities: activities.map((activity) => {
                            const { title, sortIndex, items } = activity;

                            return {
                                title,
                                sortIndex,
                                items: items.map((item) => {
                                    const {
                                        article,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions,
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    } = item;

                                    if (type === 1) {
                                        return {
                                            article: article?.videoData.articleId ?? null,
                                            sortIndex: sortIndexItem,
                                            title: titleItem,
                                            textOptions: [],
                                            type,
                                            isMultiple,
                                            rightAnswer: [],
                                            image,
                                        };
                                    }

                                    return {
                                        article: article?.videoData.articleId ?? null,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions: textOptions.map((textOption) => ({
                                            id: textOption.originId,
                                            text: textOption.text,
                                        })),
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    };
                                }),
                            };
                        }),
                    })
                );

                if (isFulfilled(action) && action.payload) {
                    setIsSubmitting(false);
                    navigate(isCustomerQuiz ? `/quiz/customer-quiz` : `/quiz/my-quiz`, {
                        replace: true,
                    });
                }

                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    }
                }
            }
        } else {
            if (quiz && isEdit) {
                const action = await dispatch(
                    editQuiz({
                        id: quiz.id,
                        data: {
                            ...rest,
                            activities:
                                activities?.map((activity) => {
                                    const { title, sortIndex, items, originId } = activity;

                                    return {
                                        id: originId,
                                        title,
                                        sortIndex,
                                        items: items?.map((item) => {
                                            const {
                                                article,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions,
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                                originId: originIdItem,
                                            } = item;

                                            if (type === 1) {
                                                return {
                                                    id: originIdItem,
                                                    article: article?.videoData.articleId ?? null,
                                                    sortIndex: sortIndexItem,
                                                    title: titleItem,
                                                    type,
                                                    isMultiple,
                                                    image,
                                                };
                                            }

                                            return {
                                                id: originIdItem,
                                                article: article?.videoData.articleId ?? null,
                                                sortIndex: sortIndexItem,
                                                title: titleItem,
                                                textOptions: textOptions.map((textOption) => ({
                                                    id: textOption.originId,
                                                    text: textOption.text,
                                                })),
                                                type,
                                                isMultiple,
                                                rightAnswer,
                                                image,
                                            };
                                        }),
                                    };
                                }) ?? [],
                        },
                    })
                );

                if (isFulfilled(action)) {
                    setIsSubmitting(false);
                    navigate(`/quiz`, { replace: true });
                }
                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action.payload.content && action.payload.content.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    } else if (action.payload.message) {
                        addNotification({
                            type: 'error',
                            title: action.payload.message,
                        });
                    }
                }
            } else {
                if (useVideoLibraryV2Create) {
                    try {
                        const formValues = methods.getValues();
                        if (!formValues.article || !formValues.articleSectionId) {
                            addNotification({
                                type: 'error',
                                title: 'Please select an article before saving.',
                            });
                            setIsSubmitting(false);
                            return;
                        }
                        const payload = buildLibraryQuizV2CreatePayload(formValues, {
                            libraryType: 'VIDEO',
                            joveDomainId: formValues.article ?? null,
                            joveSectionId: formValues.articleSectionId ?? null,
                            locale: 'en',
                        });
                        if (payload.sections.length === 0) {
                            addNotification({
                                type: 'error',
                                title: 'Add at least one multiple-choice question in a section.',
                            });
                            setIsSubmitting(false);
                            return;
                        }
                        const created = await createLibraryQuizV2(payload);
                        setIsSubmitting(false);
                        navigate(
                            afterLibraryQuizSavePath(
                                created?.quiz?.uuid,
                                payload.locale ?? created?.locale ?? 'en',
                                payload.libraryType ?? created?.libraryType,
                                '/quiz'
                            ),
                            { replace: true }
                        );
                    } catch (err: unknown) {
                        setIsSubmitting(false);
                        const ax = err as { response?: { data?: { message?: string } }; message?: string };
                        const msg =
                            ax?.response?.data?.message ??
                            (typeof ax?.message === 'string' ? ax.message : null) ??
                            'Failed to create quiz';
                        addNotification({
                            type: 'error',
                            title: typeof msg === 'string' ? msg : 'Failed to create quiz',
                        });
                    }
                    return;
                }
                const action = await dispatch(
                    createNewQuiz({
                        ...rest,
                        article: rest.article ?? 0,
                        activities: activities.map((activity) => {
                            const { title, sortIndex, items } = activity;

                            return {
                                title,
                                sortIndex,
                                items: items.map((item) => {
                                    const {
                                        article,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions,
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    } = item;

                                    if (type === 1) {
                                        return {
                                            article: article?.videoData.articleId ?? null,
                                            sortIndex: sortIndexItem,
                                            title: titleItem,
                                            textOptions: [],
                                            type,
                                            isMultiple,
                                            rightAnswer: [],
                                            image,
                                        };
                                    }

                                    return {
                                        article: article?.videoData.articleId ?? null,
                                        sortIndex: sortIndexItem,
                                        title: titleItem,
                                        textOptions: textOptions.map((textOption) => ({
                                            id: textOption.originId,
                                            text: textOption.text,
                                        })),
                                        type,
                                        isMultiple,
                                        rightAnswer,
                                        image,
                                    };
                                }),
                            };
                        }),
                    })
                );

                if (isFulfilled(action) && action.payload) {
                    setIsSubmitting(false);
                    navigate(`/quiz`, {
                        replace: true,
                    });
                }

                if (isRejected(action) && action.payload) {
                    setIsSubmitting(false);
                    if (action?.payload?.content?.errors) {
                        Object.values(action.payload.content.errors).forEach(
                            (errorItem) =>
                                errorItem?.forEach((error) => {
                                    addNotification({
                                        type: 'error',
                                        title: error.message,
                                    });
                                })
                        );
                    }
                }
            }
        }
    }, [
        dispatch,
        quiz,
        isLibrary,
        isAdminQuiz,
        isAdminQuizV2,
        isCustomerQuizV2,
        isLibraryQuizV2,
        adminQuizV2Details,
        libraryQuizV2Details,
        isEdit,
        isUserQuiz,
        methods.getValues,
        navigate,
        addNotification,
        isNewTextbookLibraryV2,
        isNewVideoLibraryV2,
        showTextbookPicker,
        libraryQuizListPath,
        queryClient,
        selectedTextbook,
        status,
        useVideoLibraryV2Create,
    ]);

    return (
        <FormProvider handleSubmit={handleSubmit} {...methods}>
            <form
                onSubmit={(event) => {
                    event?.preventDefault();
                }}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Flex w="100%" maxW="1440px" m="0 auto" justify="space-between" flexDir="row" rowGap="20px" minH="95vh" className="quiz-columns">
                        <Flex className="left-quiz-column" p="18px 50px 18px 16px" flex={'1 0 33%'} w="100%" bg="primary.grey" flexDirection="column">
                            {(isAdminQuiz && isAdminQuizV2) || isCustomerQuizV2 || isLibraryQuizV2 || isMyQuizV2 ? (
                                <LeftCreateQuizV2
                                    isLibrary={isLibrary || isAdminQuiz || isUserQuiz}
                                    isUserQuiz={isUserQuiz}
                                    isCustomerQuiz={isCustomerQuiz}
                                    isCustomerQuizV2={isCustomerQuizV2}
                                    isMyQuizV2={isMyQuizV2}
                                    isEdit={isEdit}
                                    onSubmit={onSubmit}
                                    isFormSubmitting={isSubmitting}
                                    isAssignSomeone={quiz?.isAssignSomeone ?? false}
                                    showTextbookPicker={showTextbookPicker}
                                    isNewVideoLibraryV2={isNewVideoLibraryV2}
                                    libraryType={
                                        isLibraryQuizV2 ? effectiveLibraryType : undefined
                                    }
                                    status={status}
                                    onStatusChange={setStatus}
                                    selectedTextbook={selectedTextbook}
                                    onTextbookSelect={(tb) => {
                                        setSelectedTextbook(tb);
                                        setTextbookError(undefined);
                                    }}
                                    textbookError={textbookError}
                                    articleError={articleError}
                                    onArticleSelect={() => setArticleError(undefined)}
                                />
                            ) : (
                                <LeftCreateQuiz
                                    isLibrary={isLibrary || isAdminQuiz || isUserQuiz}
                                    isUserQuiz={isUserQuiz}
                                    isCustomerQuiz={isCustomerQuiz}
                                    isEdit={isEdit}
                                    onSubmit={onSubmit}
                                    isFormSubmitting={isSubmitting}
                                    isAssignSomeone={quiz?.isAssignSomeone ?? false}
                                    libraryV2Select={libraryV2Select}
                                />
                            )}
                        </Flex>
                        <Flex className="right-quiz-column" w="100%" flex={'1 0 67%'} bg="white" flexDirection="column">
                            <QuizMathJaxProvider>
                                <RightQuiz isLibrary={isLibrary || isAdminQuiz || isUserQuiz} />
                            </QuizMathJaxProvider>
                        </Flex>
                    </Flex>
                </DragDropContext>
            </form>
        </FormProvider>
    );
  }
);
